import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { useLanguage } from '../../../context/LanguageContext';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '../../../components/ui/dialog';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../components/ui/select';
import { Switch } from '../../../components/ui/switch';
import {
  Dumbbell, Wind, ShieldCheck, Heart, Search, Plus, Pencil, Trash2, X, ChevronLeft,
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const CATEGORIES = ['ALL', 'STRENGTH', 'AEROBIC', 'RTP', 'RECOVERY'];
const CAT_COLORS = {
  STRENGTH: 'bg-blue-500/15 text-blue-400 border-blue-500/30',
  AEROBIC: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
  RTP: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
  RECOVERY: 'bg-purple-500/15 text-purple-400 border-purple-500/30',
};
const CAT_ICONS = { STRENGTH: Dumbbell, AEROBIC: Wind, RTP: ShieldCheck, RECOVERY: Heart };

export default function PrescriptionLibraryPage() {
  const { api, hasRole } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [exercises, setExercises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('ALL');
  const [showCreate, setShowCreate] = useState(false);
  const [editExercise, setEditExercise] = useState(null);
  const canEdit = hasRole(['ADMIN', 'SNC', 'PHYSIO']);

  const [form, setForm] = useState({
    name: '', category: 'STRENGTH', description: '', notes: '', equipment: '',
    tags: '', is_vbt_linked: false, vbt_lift_type: '',
    default_sets: '', default_reps: '', default_duration: '',
  });

  const fetchExercises = useCallback(async () => {
    try {
      const params = {};
      if (category !== 'ALL') params.category = category;
      if (search) params.search = search;
      const res = await api.get('/exercises', { params });
      setExercises(res.data);
    } catch (e) {
      toast.error('Failed to load exercises');
    } finally {
      setLoading(false);
    }
  }, [api, category, search]);

  useEffect(() => { fetchExercises(); }, [fetchExercises]);

  const openCreate = () => {
    setForm({ name: '', category: 'STRENGTH', description: '', notes: '', equipment: '', tags: '', is_vbt_linked: false, vbt_lift_type: '', default_sets: '', default_reps: '', default_duration: '' });
    setEditExercise(null);
    setShowCreate(true);
  };

  const openEdit = (ex) => {
    setForm({
      name: ex.name, category: ex.category, description: ex.description || '',
      notes: ex.notes || '', equipment: ex.equipment || '', tags: (ex.tags || []).join(', '),
      is_vbt_linked: ex.is_vbt_linked || false, vbt_lift_type: ex.vbt_lift_type || '',
      default_sets: ex.default_sets || '', default_reps: ex.default_reps || '', default_duration: ex.default_duration || '',
    });
    setEditExercise(ex);
    setShowCreate(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    const payload = {
      name: form.name.trim(),
      category: form.category,
      description: form.description || null,
      notes: form.notes || null,
      equipment: form.equipment || null,
      tags: form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
      is_vbt_linked: form.is_vbt_linked,
      vbt_lift_type: form.vbt_lift_type || null,
      default_sets: form.default_sets ? parseInt(form.default_sets) : null,
      default_reps: form.default_reps ? parseInt(form.default_reps) : null,
      default_duration: form.default_duration ? parseInt(form.default_duration) : null,
    };
    try {
      if (editExercise) {
        await api.put(`/exercises/${editExercise.id}`, payload);
        toast.success('Exercise updated');
      } else {
        await api.post('/exercises', payload);
        toast.success('Exercise created');
      }
      setShowCreate(false);
      fetchExercises();
    } catch (e) {
      toast.error('Failed to save exercise');
    }
  };

  const handleDelete = async (ex) => {
    if (!window.confirm(`Delete "${ex.name}"?`)) return;
    try {
      await api.delete(`/exercises/${ex.id}`);
      toast.success('Exercise deleted');
      fetchExercises();
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  const filtered = exercises;

  return (
    <div className="space-y-6" data-testid="prescription-library-page">
      <div className="flex items-center gap-3 mb-2">
        <Button variant="ghost" size="sm" onClick={() => navigate('/staff/prescriptions')} className="text-zinc-400 hover:text-zinc-100" data-testid="back-to-prescriptions">
          <ChevronLeft className="w-4 h-4 mr-1" /> Back
        </Button>
      </div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-zinc-100" data-testid="library-title">Exercise Library</h1>
          <p className="text-sm text-zinc-500 mt-1">{exercises.length} exercises available</p>
        </div>
        {canEdit && (
          <Button onClick={openCreate} className="bg-amber-500 hover:bg-amber-600 text-black" data-testid="add-exercise-btn">
            <Plus className="w-4 h-4 mr-2" /> Add Exercise
          </Button>
        )}
      </div>

      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
        <div className="relative flex-1 w-full sm:max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <Input
            placeholder="Search exercises..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 bg-zinc-900 border-zinc-800 text-zinc-100"
            data-testid="library-search"
          />
        </div>
        <div className="flex gap-1.5 flex-wrap">
          {CATEGORIES.map(c => (
            <button
              key={c}
              onClick={() => setCategory(c)}
              className={`px-3 py-1.5 text-xs font-medium rounded-sm transition-colors ${
                category === c ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30' : 'bg-zinc-900 text-zinc-400 border border-zinc-800 hover:border-zinc-700'
              }`}
              data-testid={`filter-${c.toLowerCase()}`}
            >
              {c === 'ALL' ? 'All' : c}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><div className="text-zinc-500">Loading...</div></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-zinc-500">No exercises found</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="exercise-grid">
          {filtered.map(ex => {
            const Icon = CAT_ICONS[ex.category] || Dumbbell;
            return (
              <Card key={ex.id} className="bg-zinc-900/50 border-zinc-800 hover:border-zinc-700 transition-colors" data-testid={`exercise-card-${ex.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className={`p-1.5 rounded-sm ${CAT_COLORS[ex.category] || 'bg-zinc-800 text-zinc-400'}`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-zinc-100 text-sm">{ex.name}</h3>
                        <Badge variant="outline" className={`text-[10px] mt-0.5 ${CAT_COLORS[ex.category]}`}>{ex.category}</Badge>
                      </div>
                    </div>
                    {canEdit && (
                      <div className="flex gap-1">
                        <button onClick={() => openEdit(ex)} className="p-1 text-zinc-500 hover:text-zinc-300" data-testid={`edit-exercise-${ex.id}`}><Pencil className="w-3.5 h-3.5" /></button>
                        <button onClick={() => handleDelete(ex)} className="p-1 text-zinc-500 hover:text-rose-400" data-testid={`delete-exercise-${ex.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    )}
                  </div>
                  {ex.description && <p className="text-xs text-zinc-500 mb-2 line-clamp-2">{ex.description}</p>}
                  {ex.notes && <p className="text-xs text-zinc-600 mb-2 italic">{ex.notes}</p>}
                  {ex.equipment && <p className="text-xs text-zinc-600"><span className="text-zinc-500">Equipment:</span> {ex.equipment}</p>}
                  {(ex.default_sets || ex.default_reps || ex.default_duration) && (
                    <div className="flex gap-2 mt-1.5 text-[10px] text-zinc-500">
                      {ex.default_sets && <span>{ex.default_sets} sets</span>}
                      {ex.default_reps && <span>{ex.default_reps} reps</span>}
                      {ex.default_duration && <span>{ex.default_duration}s</span>}
                    </div>
                  )}
                  <div className="flex flex-wrap gap-1 mt-2">
                    {ex.is_vbt_linked && <Badge className="bg-blue-500/10 text-blue-400 border-blue-500/20 text-[10px]">VBT Linked</Badge>}
                    {(ex.tags || []).slice(0, 3).map(tag => (
                      <Badge key={tag} variant="outline" className="text-[10px] border-zinc-700 text-zinc-500">{tag}</Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md" data-testid="exercise-dialog">
          <DialogHeader>
            <DialogTitle className="text-zinc-100">{editExercise ? 'Edit Exercise' : 'New Exercise'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-zinc-400">Name</Label>
              <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="bg-zinc-800 border-zinc-700 text-zinc-100" data-testid="exercise-name-input" />
            </div>
            <div>
              <Label className="text-zinc-400">Category</Label>
              <Select value={form.category} onValueChange={v => setForm({...form, category: v})}>
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-100" data-testid="exercise-category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  {['STRENGTH','AEROBIC','RTP','RECOVERY'].map(c => (
                    <SelectItem key={c} value={c} className="text-zinc-100">{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-zinc-400">Description</Label>
              <Textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="bg-zinc-800 border-zinc-700 text-zinc-100" rows={2} data-testid="exercise-desc-input" />
            </div>
            <div>
              <Label className="text-zinc-400">Notes</Label>
              <Input value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} className="bg-zinc-800 border-zinc-700 text-zinc-100" placeholder="Any extra notes..." data-testid="exercise-notes-input" />
            </div>
            <div>
              <Label className="text-zinc-400">Equipment</Label>
              <Input value={form.equipment} onChange={e => setForm({...form, equipment: e.target.value})} className="bg-zinc-800 border-zinc-700 text-zinc-100" data-testid="exercise-equipment-input" />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label className="text-zinc-400 text-[11px]">Default Sets</Label>
                <Input type="number" value={form.default_sets} onChange={e => setForm({...form, default_sets: e.target.value})} className="bg-zinc-800 border-zinc-700 text-zinc-100 h-8 text-xs" data-testid="exercise-default-sets" />
              </div>
              <div>
                <Label className="text-zinc-400 text-[11px]">Default Reps</Label>
                <Input type="number" value={form.default_reps} onChange={e => setForm({...form, default_reps: e.target.value})} className="bg-zinc-800 border-zinc-700 text-zinc-100 h-8 text-xs" data-testid="exercise-default-reps" />
              </div>
              <div>
                <Label className="text-zinc-400 text-[11px]">Default Duration (sec)</Label>
                <Input type="number" value={form.default_duration} onChange={e => setForm({...form, default_duration: e.target.value})} className="bg-zinc-800 border-zinc-700 text-zinc-100 h-8 text-xs" data-testid="exercise-default-duration" />
              </div>
            </div>
            <div>
              <Label className="text-zinc-400">Tags (comma-separated)</Label>
              <Input value={form.tags} onChange={e => setForm({...form, tags: e.target.value})} className="bg-zinc-800 border-zinc-700 text-zinc-100" placeholder="e.g. lower_body, compound" data-testid="exercise-tags-input" />
            </div>
            <div className="flex items-center justify-between">
              <Label className="text-zinc-400">VBT Linked</Label>
              <Switch checked={form.is_vbt_linked} onCheckedChange={v => setForm({...form, is_vbt_linked: v})} data-testid="exercise-vbt-switch" />
            </div>
            {form.is_vbt_linked && (
              <div>
                <Label className="text-zinc-400">VBT Lift Type</Label>
                <Input value={form.vbt_lift_type} onChange={e => setForm({...form, vbt_lift_type: e.target.value})} className="bg-zinc-800 border-zinc-700 text-zinc-100" placeholder="e.g. BACK_SQUAT" data-testid="exercise-vbt-type-input" />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)} className="border-zinc-700 text-zinc-400" data-testid="exercise-cancel-btn">Cancel</Button>
            <Button onClick={handleSave} className="bg-amber-500 hover:bg-amber-600 text-black" data-testid="exercise-save-btn">
              {editExercise ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
